/**
 * WebhookConfig Component
 *
 * Configuration for webhook notification including URL and custom headers.
 */

import React, { useState, useCallback } from 'react';
import { clsx } from 'clsx';

export interface WebhookConfigValues {
  webhook_url: string;
  webhook_headers: Record<string, string>;
  notification_type: string;
  cooldown_minutes: number;
}

export interface WebhookConfigProps {
  value: WebhookConfigValues;
  onChange: (value: WebhookConfigValues) => void;
  disabled?: boolean;
  className?: string;
}

export const WebhookConfig: React.FC<WebhookConfigProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
}) => {
  const [showHeaders, setShowHeaders] = useState(
    Object.keys(value.webhook_headers).length > 0
  );
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');

  const handleUrlChange = useCallback(
    (url: string) => {
      onChange({
        ...value,
        webhook_url: url,
        notification_type: url.trim() ? 'webhook' : 'log',
      });
    },
    [value, onChange]
  );

  const handleCooldownChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val) && val >= 1) {
        onChange({ ...value, cooldown_minutes: val });
      }
    },
    [value, onChange]
  );

  const addHeader = useCallback(() => {
    const key = headerKey.trim();
    const val = headerValue.trim();
    if (key && val) {
      onChange({
        ...value,
        webhook_headers: { ...value.webhook_headers, [key]: val },
      });
      setHeaderKey('');
      setHeaderValue('');
    }
  }, [headerKey, headerValue, value, onChange]);

  const removeHeader = useCallback(
    (key: string) => {
      const newHeaders = { ...value.webhook_headers };
      delete newHeaders[key];
      onChange({ ...value, webhook_headers: newHeaders });
    },
    [value, onChange]
  );

  return (
    <div className={clsx('space-y-4', className)}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          通知方式
        </label>
        <select
          value={value.notification_type}
          onChange={(e) => onChange({ ...value, notification_type: e.target.value })}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
        >
          <option value="log">日志 (Log)</option>
          <option value="webhook">Webhook</option>
        </select>
      </div>

      {value.notification_type === 'webhook' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Webhook URL
          </label>
          <input
            type="url"
            value={value.webhook_url}
            onChange={(e) => handleUrlChange(e.target.value)}
            disabled={disabled}
            placeholder="https://your-webhook-url/endpoint"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
          />
          <p className="mt-1 text-xs text-gray-500">
            触发告警时向此 URL 发送 POST 请求
          </p>

          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowHeaders(!showHeaders)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {showHeaders ? '收起' : '自定义请求头'}
            </button>
          </div>

          {showHeaders && (
            <div className="mt-2 space-y-2">
              {Object.entries(value.webhook_headers).map(([key, val]) => (
                <div
                  key={key}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="font-mono text-gray-600">{key}:</span>
                  <span className="font-mono text-gray-800">{val}</span>
                  <button
                    type="button"
                    onClick={() => removeHeader(key)}
                    disabled={disabled}
                    className="text-red-400 hover:text-red-600 disabled:opacity-50"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={headerKey}
                  onChange={(e) => setHeaderKey(e.target.value)}
                  disabled={disabled}
                  placeholder="Header Name"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-50"
                />
                <input
                  type="text"
                  value={headerValue}
                  onChange={(e) => setHeaderValue(e.target.value)}
                  disabled={disabled}
                  placeholder="Header Value"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-50"
                />
                <button
                  type="button"
                  onClick={addHeader}
                  disabled={disabled || !headerKey.trim() || !headerValue.trim()}
                  className="px-2 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  添加
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          冷却时间（分钟）
        </label>
        <input
          type="number"
          min={1}
          value={value.cooldown_minutes}
          onChange={handleCooldownChange}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
        />
        <p className="mt-1 text-xs text-gray-500">
          同一告警的最小发送间隔
        </p>
      </div>
    </div>
  );
};

export default WebhookConfig;
